import React, { useState, useEffect, useMemo, useRef } from "react";
import type { Row } from "react-table";
import { useScrollSpy, type UseScrollSpyHeadingData } from "@mantine/hooks";
import ReactTable from "./ReactTable";
import { rowStyle, rowClassName, getInitialState, TagsTableSearchContext } from "./ReactTableTags";
import { translate } from "../IntlProviderWrapper";
import type { ReactTableColumn } from "./ReactTable";
import type { TagsData, TagUpdateValue } from "./ReactTableTags";
import { classNames, matchesSearch } from "../../lib/Util";
import { HighlightedText } from "./HighlightedText";

import "./TagsTables.scss";

const SEARCH_DEBOUNCE_MS = 300;
const SCROLL_HOST_SELECTOR = ".grid-main-content";
const SCROLL_SELECTOR = "section [data-heading]";

type Props = {
    categories: { name: string; title: string; rows: TagsData[] }[];
    columns: ReactTableColumn<TagsData>[];
    onChangeCell?: (name: string) => (value: TagUpdateValue) => void;
    /** Override default rowStyle (from ReactTableTags). */
    rowStyle?: (row: Row<TagsData>) => React.CSSProperties;
    /** Optional prefix for React key when rendering multiple TagsTables instances (e.g. "man-" or "gui-"). */
    categoryKeyPrefix?: string;
    stretchHeight?: boolean;
    /** Unknown tag keys from the entity; shown in a second block below standard tags when non-empty. */
    customTags?: TagsData[];
};

/**
 * Displays each set of tags as a separate table using the ReactTable component, grouping them by category.
 * Uses Scrollspy-style nav with smooth scroll and active highlight on scroll.
 */
function TagsTables(props: Props) {
    const {
        categories,
        columns,
        onChangeCell,
        rowStyle: rowStyleProp,
        categoryKeyPrefix = "",
        stretchHeight = true,
        customTags = [],
    } = props;
    const hasCustomTags = customTags.length > 0;
    const mainStretchHeight = stretchHeight && !hasCustomTags;
    const contentRef = useRef<HTMLDivElement>(null);
    const scrollSpy = useScrollSpy({
        selector: SCROLL_SELECTOR,
        scrollHost: document.querySelector(SCROLL_HOST_SELECTOR) as HTMLElement,
        getValue: element => element.getAttribute("data-heading") || "",
    });
    // Used for tracking input as the user types
    const [searchInput, setSearchInput] = useState("");
    // Debounced query used for filtering tags and categories
    const [searchQuery, setSearchQuery] = useState("");
    /** Ref for debounced scroll: avoids recreating debouncers on focus churn (stale timers → setState after unmount). */
    const inputFocusedRef = useRef(false);

    const filteredCategories = useMemo(
        () =>
            categories
                .map(category => ({
                    ...category,
                    rows: category.rows.filter(row =>
                        matchesSearch([row.key, row.title, row.comment].join(" "), searchQuery)
                    ),
                }))
                .filter(category => category.rows.length > 0),
        [categories, searchQuery]
    );

    useEffect(() => {
        const id = setTimeout(() => {
            if (inputFocusedRef.current) {
                contentRef.current?.scrollIntoView({ behavior: "smooth" });
            }
            setSearchQuery(searchInput);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [searchInput]);

    useEffect(() => {
        scrollSpy.reinitialize();
    }, [filteredCategories]);

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
    };

    const handleClick = (heading: UseScrollSpyHeadingData) => {
        heading.getNode().scrollIntoView({ behavior: "smooth" });
    };

    const highlight = searchQuery && searchQuery.trim();

    return (
        <>
            <div className="tags-tables-wrapper">
                <div className="tags-tables-nav">
                    <div className="tags-tables-search mb-2">
                        <i className="tags-tables-search__icon fas fa-search" aria-hidden />
                        <input
                            onFocus={() => {
                                inputFocusedRef.current = true;
                            }}
                            onBlur={() => {
                                inputFocusedRef.current = false;
                            }}
                            type="search"
                            className="form-control tags-tables-search__input"
                            placeholder={translate("common.search", "Search")}
                            value={searchInput}
                            onChange={handleSearchInputChange}
                            aria-label={translate("common.search", "Search")}
                        />
                    </div>
                    {categories.length > 1 && (
                        <div className="list-group">
                            {scrollSpy.data.map((heading, index) => {
                                const title = translate(`tag_groups.categories.title.${heading.value}`);

                                return (
                                    <button
                                        key={`${categoryKeyPrefix}${heading.id}`}
                                        className={`list-group-item ${scrollSpy.active === index ? "active" : ""}`}
                                        onClick={() => handleClick(heading)}
                                    >
                                        {highlight ? (
                                            <HighlightedText text={title ?? ""} query={highlight ?? ""} />
                                        ) : (
                                            title
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <TagsTableSearchContext.Provider value={searchQuery}>
                    <div
                        className={classNames("tags-tables-content table-responsive", {
                            "stretch-height": mainStretchHeight,
                        })}
                        ref={contentRef}
                    >
                        {filteredCategories.length > 0 ? (
                            filteredCategories.map(category => (
                                <section key={`${categoryKeyPrefix}${category.name}`}>
                                    {category.name && (
                                        <h4 className="table-section-header" data-heading={category.name}>
                                            {translate(`tag_groups.categories.title.${category.name}`)}
                                        </h4>
                                    )}

                                    <ReactTable
                                        className="tags-react-table"
                                        data={category.rows}
                                        columns={columns}
                                        onChangeCell={onChangeCell}
                                        rowStyle={rowStyleProp ?? rowStyle}
                                        rowClassName={rowClassName}
                                        initialState={getInitialState()}
                                        hideHeader={true}
                                    />
                                </section>
                            ))
                        ) : (
                            <div className="tags-tables-no-data">
                                {translate("common.no_tags_found", "No tags found")}
                            </div>
                        )}
                    </div>
                </TagsTableSearchContext.Provider>
            </div>
            {hasCustomTags && (
                <>
                    <h2 className="mb-4 mt-4">{translate("common.nonstandard_data")}</h2>
                    <TagsTables
                        categories={[{ name: "", title: "", rows: customTags }]}
                        columns={columns}
                        onChangeCell={onChangeCell}
                        rowStyle={rowStyleProp}
                        categoryKeyPrefix={`${categoryKeyPrefix}nonstd-`}
                        stretchHeight={stretchHeight}
                    />
                </>
            )}
        </>
    );
}

export default TagsTables;
